import { CurrencyDollarSimpleIcon, PercentIcon } from '@phosphor-icons/react'
import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import get from 'lodash/get'
import { useTranslation } from 'react-i18next'

import RadioField from '../../../FormLegacy/FormFieldsV2/RadioField'
import SelectField from '../../../FormLegacy/FormFieldsV2/SelectField'
import ModalContainer from '../../../ModalContainer/ModelContainer'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import ToggleField from '../../../FormLegacy/FormFieldsV2/ToggleField'

import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'

import API from '../../../../api/api'
import {
  CONVENIENCE_FEE_MAX_PERCENTAGE,
  getConvenienceFeesPayload,
  getPayRentConfigInitialValues,
  getPayRentConfigValidationSchema,
  toFloatWithDecimals,
} from '../helpers'

const PERCENTAGE_DECIMALS = 1
const FLAT_AMOUNT_DECIMALS = 2

const MIN_DELAY = 0
const MAX_DELAY = 90

const PayRentConfigModal = ({ onClose = () => {}, buildingId, isRentManager = false }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [paymentSettings, setPaymentSettings] = useState(null)
  const [ledgerCodes, setLedgerCodes] = useState([])
  const { displaySnackbar } = useSnackbar()
  const { t } = useTranslation(['organisation', 'common'])

  const convenienceFeeRows = [
    {
      percentageLabel: t(
        'organisation:places.integrations.payRentConfig.convenienceFee.cardPercentage',
      ),
      flatAmountLabel: t(
        'organisation:places.integrations.payRentConfig.convenienceFee.cardFlatAmount',
      ),
      percentageField: 'cardFeePercentage',
      flatAmountField: 'cardFeeFlatAmount',
    },
    {
      percentageLabel: t(
        'organisation:places.integrations.payRentConfig.convenienceFee.achPercentage',
      ),
      flatAmountLabel: t(
        'organisation:places.integrations.payRentConfig.convenienceFee.achFlatAmount',
      ),
      percentageField: 'achFeePercentage',
      flatAmountField: 'achFeeFlatAmount',
    },
  ]

  const onSubmit = async (values) => {
    setIsLoading(true)
    try {
      const {
        cardFeePercentage,
        cardFeeFlatAmount,
        achFeePercentage,
        achFeeFlatAmount,
        chargeCodesMappings,
        ...valuesToSubmit
      } = values
      const processingFees = getConvenienceFeesPayload({
        cardFeePercentage,
        cardFeeFlatAmount,
        achFeePercentage,
        achFeeFlatAmount,
      })

      const data = {
        settings: {
          ...valuesToSubmit,
        },
        processingFees,
        chargeCodesMappings,
      }

      await API.updatePaymentSettings(buildingId, data)
      displaySnackbar(t('organisation:places.integrations.payRentConfig.toasts.saved'), false)
      onClose()
    } catch {
      displaySnackbar(t('organisation:places.integrations.payRentConfig.toasts.saveFailed'), true)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPaymentSettings = async () => {
    if (!buildingId) return
    setIsLoading(true)
    try {
      const { data } = await API.fetchPaymentSettings(buildingId)
      const { chargeCodesMappings = [] } = data
      setLedgerCodes(chargeCodesMappings.map(({ code }) => ({ value: code, label: code })))
      setPaymentSettings(data)
    } catch (error) {
      console.error('Error fetching payment settings:', error)
      displaySnackbar(t('organisation:places.integrations.payRentConfig.toasts.fetchFailed'), true)
    } finally {
      setIsLoading(false)
    }
  }

  // A fee is either a percentage or a flat amount, never both: typing into one input clears the
  // other, so the filled input is always the active mode.
  const handleFeeChange = ({ name, counterpartName, value, decimals, max }, setFieldValue) => {
    if (value === '') {
      setFieldValue(name, '')
      return
    }

    const formattedValue = toFloatWithDecimals(value, decimals)

    if (formattedValue !== null) {
      setFieldValue(name, max ? Math.min(formattedValue, max) : formattedValue)
      setFieldValue(counterpartName, '')
    }
  }

  const handleProcessingDelayChange = (name, value, setFieldValue) => {
    if (value === '') {
      setFieldValue(name, '')
      return
    }

    const numValue = parseInt(value)
    if (!isNaN(numValue)) {
      setFieldValue(name, Math.min(Math.max(numValue, MIN_DELAY), MAX_DELAY))
    }
  }

  const getLedgerCodes = useCallback(
    (codeToExclude) => {
      return ledgerCodes.filter(({ value }) => value !== codeToExclude)
    },
    [ledgerCodes],
  )

  useEffect(() => {
    if (buildingId) {
      fetchPaymentSettings()
    }
  }, [buildingId])

  return (
    <ModalContainer
      additionalClass="ctp-integration-modal ctp-integration-modal--pay-rent"
      onClose={onClose}
      headerTitle={t('organisation:places.integrations.payRentConfig.title')}
    >
      <Formik
        enableReinitialize
        initialValues={paymentSettings ? getPayRentConfigInitialValues(paymentSettings) : {}}
        validationSchema={getPayRentConfigValidationSchema(t, isRentManager)}
        onSubmit={onSubmit}
      >
        {({
          values,
          isValid,
          errors,
          dirty,
          handleSubmit,
          handleChange,
          setFieldValue,
          isSubmitting,
        }) => (
          <React.Fragment>
            <form className="integration-modal-content">
              <span className="integration-modal-content__title">
                {t('organisation:places.integrations.payRentConfig.convenienceFee.title')}
              </span>
              {convenienceFeeRows.map(
                ({ percentageLabel, flatAmountLabel, percentageField, flatAmountField }) => (
                  <div className="integration-modal-content__fee-row" key={percentageField}>
                    <div className="integration-modal-content__fee-input">
                      <TextField
                        name={percentageField}
                        label={percentageLabel}
                        leftIcon={
                          <span className="integration-modal-content__fee-icon">
                            <PercentIcon />
                          </span>
                        }
                        type="number"
                        value={values[percentageField]}
                        placeholder={t(
                          'organisation:places.integrations.payRentConfig.convenienceFee.percentagePlaceholder',
                        )}
                        onChange={(e) =>
                          handleFeeChange(
                            {
                              name: percentageField,
                              counterpartName: flatAmountField,
                              value: e.target.value,
                              decimals: PERCENTAGE_DECIMALS,
                              max: CONVENIENCE_FEE_MAX_PERCENTAGE,
                            },
                            setFieldValue,
                          )
                        }
                        isError={!!errors[percentageField]}
                        errorLabel={errors[percentageField]}
                      />
                    </div>
                    <span className="input-label integration-modal-content__fee-or">
                      {t('organisation:places.integrations.payRentConfig.convenienceFee.or')}
                    </span>
                    <div className="integration-modal-content__fee-input">
                      <TextField
                        name={flatAmountField}
                        label={flatAmountLabel}
                        leftIcon={
                          <span className="integration-modal-content__fee-icon">
                            <CurrencyDollarSimpleIcon />
                          </span>
                        }
                        type="number"
                        value={values[flatAmountField]}
                        placeholder={t(
                          'organisation:places.integrations.payRentConfig.convenienceFee.flatAmountPlaceholder',
                        )}
                        onChange={(e) =>
                          handleFeeChange(
                            {
                              name: flatAmountField,
                              counterpartName: percentageField,
                              value: e.target.value,
                              decimals: FLAT_AMOUNT_DECIMALS,
                            },
                            setFieldValue,
                          )
                        }
                        isError={!!errors[flatAmountField]}
                        errorLabel={errors[flatAmountField]}
                      />
                    </div>
                  </div>
                ),
              )}
              <div className="integration-modal-content__divider" />
              {!isRentManager && (
                <SelectField
                  name="prePaidChargeCode"
                  options={getLedgerCodes(values.amenityChargeCode)}
                  placeholder={t(
                    'organisation:places.integrations.payRentConfig.prePaidChargeCode.placeholder',
                  )}
                  label={t(
                    'organisation:places.integrations.payRentConfig.prePaidChargeCode.label',
                  )}
                  onChange={handleChange}
                  value={values.prePaidChargeCode}
                />
              )}
              <div className="integration-modal-content__field">
                <span className="input-label">
                  {t('organisation:places.integrations.payRentConfig.dailyBatch.label')}
                </span>
                <div className="integration-modal-content__radios">
                  <RadioField
                    type="radio"
                    name="individual"
                    value="individual"
                    label={t(
                      'organisation:places.integrations.payRentConfig.dailyBatch.individual',
                    )}
                    onChange={() => setFieldValue('dailyBatch', false)}
                    checked={!values.dailyBatch}
                  />
                  {!isRentManager && (
                    <RadioField
                      type="radio"
                      name="batch"
                      value="batch"
                      label={t('organisation:places.integrations.payRentConfig.dailyBatch.batch')}
                      onChange={() => setFieldValue('dailyBatch', true)}
                      checked={values.dailyBatch}
                    />
                  )}
                </div>
              </div>
              {!isRentManager && (
                <SelectField
                  name="amenityChargeCode"
                  options={getLedgerCodes(values.prePaidChargeCode)}
                  placeholder={t(
                    'organisation:places.integrations.payRentConfig.amenityChargeCode.placeholder',
                  )}
                  label={t(
                    'organisation:places.integrations.payRentConfig.amenityChargeCode.label',
                  )}
                  onChange={handleChange}
                  value={values.amenityChargeCode}
                  error={errors.amenityChargeCode}
                />
              )}
              <div className="integration-modal-content__field">
                <span className="input-label">
                  {t('organisation:places.integrations.payRentConfig.processingDelay.label')}
                </span>
                <TextField
                  type="number"
                  name="processingDelay"
                  value={values.processingDelay}
                  placeholder={t(
                    'organisation:places.integrations.payRentConfig.processingDelay.placeholder',
                  )}
                  onChange={(e) =>
                    handleProcessingDelayChange('processingDelay', e.target.value, setFieldValue)
                  }
                  isError={!!errors.processingDelay}
                  errorLabel={errors.processingDelay}
                />
              </div>
              <div className="integration-modal-content__divider" />
              <div className="integration-modal-content__toggles">
                <ToggleField
                  label={t('organisation:places.integrations.payRentConfig.allowAutopay')}
                  name="allowAutopay"
                  labelOnRight
                  onChange={handleChange}
                  value={values.allowAutopay}
                  error={errors.allowAutopay}
                />
                <ToggleField
                  label={t('organisation:places.integrations.payRentConfig.allowChargesSelection')}
                  name="allowChargesSelection"
                  labelOnRight
                  onChange={handleChange}
                  value={values.allowChargesSelection}
                />
                <ToggleField
                  label={t('organisation:places.integrations.payRentConfig.allowPartialPayment')}
                  name="allowPartialPayment"
                  labelOnRight
                  onChange={handleChange}
                  value={values.allowPartialPayment}
                />
              </div>
              <div className="integration-modal-content__divider" />
              <span className="integration-modal-content__title">
                {t('organisation:places.integrations.payRentConfig.chargeCodesMappings.title')}
              </span>
              {values.chargeCodesMappings &&
                values.chargeCodesMappings.map(({ code, description }, index) => (
                  <div className="integration-modal-content__ledger-row" key={code}>
                    <span className="input-label">{code}</span>
                    <TextField
                      name={`chargeCodesMappings[${index}].description`}
                      value={description}
                      placeholder={t(
                        'organisation:places.integrations.payRentConfig.chargeCodesMappings.placeholder',
                      )}
                      onChange={handleChange}
                      isError={!!get(errors, `chargeCodesMappings[${index}].description`)}
                      errorLabel={get(errors, `chargeCodesMappings[${index}].description`)}
                    />
                  </div>
                ))}
            </form>
            <div className="integration-modal-actions">
              <button onClick={onClose} type="button" className="button button-gray">
                {t('common:actions.cancel')}
              </button>
              <button
                className={`button button-black ${isSubmitting ? 'is-loading' : ''}`}
                type="button"
                disabled={!isValid || !dirty || isSubmitting || isLoading}
                onClick={handleSubmit}
              >
                {t('common:actions.save')}
              </button>
            </div>
          </React.Fragment>
        )}
      </Formik>
    </ModalContainer>
  )
}

PayRentConfigModal.propTypes = {
  onClose: PropTypes.func,
  buildingId: PropTypes.number.isRequired,
  isRentManager: PropTypes.bool,
}

export default PayRentConfigModal
